import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, FormField, Modal, Select } from '@/components/ui';
import { RoleMapStaleCatalogCard } from './RoleMapStaleCatalogCard';
import { isStaleCatalogError } from './useRoleMapWrites';
import type { RoleMapTableVM } from './useRoleMapTable';

/**
 * EAA-1817: "Đổi vai trò" (frame 45) — re-points a link at a different
 * `app_role` in one PATCH instead of the old delete-then-create two-step.
 *
 * Scoped to `vm.repointTarget`, a single-id `RoleMapGroup` — see
 * `useRoleMapTable`'s `openRepoint` docstring for why multi-role groups never
 * reach this modal (no single unambiguous target id to PATCH). `PATCH
 * /role-maps/{id}` is `IsSuperAdmin`-only server-side, so the row action that
 * opens this is only ever offered when `vm.canManageDefault` — this modal
 * does not re-check that itself, matching how `RoleMapAddModal`/the bulk bar
 * trust their own callers' gating rather than duplicating the check.
 */
export function RoleMapRepointModal({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const group = vm.repointTarget;
    const open = Boolean(group);
    const currentAppRole = group?.appRoles[0]?.app_role ?? null;
    const [selected, setSelected] = useState('');

    useEffect(() => {
        if (!open) return;
        setSelected('');
    }, [open, group?.key]);

    const { error: repointError, reset: resetRepointError } = vm.repointMutation;
    const staleError = isStaleCatalogError(repointError) ? repointError : null;

    if (!group) return null;

    const submit = (appRole: string) => {
        if (!appRole || appRole === currentAppRole) return;
        vm.repointMutation.mutate(
            { id: group.ids[0], appRole, application: group.application },
            { onSuccess: () => vm.closeRepoint() }
        );
    };

    return (
        <Modal
            open={open}
            onCancel={vm.closeRepoint}
            title={t('console.roleMap.repointModal.title', {
                roleKey: group.role_key,
                appName: group.appName,
            })}
            footer={
                <>
                    <Button onClick={vm.closeRepoint} disabled={vm.repointMutation.isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        disabled={
                            !selected || selected === currentAppRole || vm.repointMutation.isPending
                        }
                        onClick={() => submit(selected)}
                    >
                        {vm.repointMutation.isPending
                            ? t('console.roleMap.repointModal.saving')
                            : t('console.roleMap.repointModal.submitButton')}
                    </Button>
                </>
            }
        >
            <div className="portal-admin-form" style={{ gridTemplateColumns: '1fr' }}>
                <p className="portal-admin-hint">
                    <Trans
                        i18nKey="console.roleMap.repointModal.hint"
                        values={{ currentAppRole: currentAppRole ?? '—', appName: group.appName }}
                        components={[<code key="role" />]}
                    />
                </p>
                <FormField label={t('console.roleMap.repointModal.fieldLabel')}>
                    {/* EAA-1817: kit `Select`'s `onChange` passes the value, not a DOM
                    event — the loading placeholder is modeled as a disabled option with
                    a sentinel value (never a real `app_role`) rather than antd's own
                    loading affordance, to keep the exact same three option groups
                    (placeholder / loading / catalog) the native select rendered. */}
                    <Select
                        value={selected}
                        onChange={(value) => setSelected(value as string)}
                        style={{ width: '100%' }}
                        options={[
                            { value: '', label: t('console.roleMap.repointModal.placeholderOption') },
                            ...(vm.repointAppRolesLoading
                                ? [
                                      {
                                          value: '__loading__',
                                          label: t('console.roleMap.repointModal.loadingCatalogOption'),
                                          disabled: true,
                                      },
                                  ]
                                : []),
                            ...vm.repointAppRoles.map((role) => ({
                                value: role.key,
                                label:
                                    role.key +
                                    (role.key === currentAppRole
                                        ? ` ${t('console.roleMap.repointModal.currentSuffix')}`
                                        : ''),
                                disabled: role.key === currentAppRole,
                            })),
                        ]}
                    />
                </FormField>

                {staleError && (
                    // EAA-1817: no `onRetry` here, unlike `RoleMapAddModal`'s card —
                    // a re-point picks exactly ONE replacement, so "Thử lại" would
                    // only ever resubmit the same now-invalid key and fail
                    // identically. "Đồng bộ danh mục" refreshes the picker's
                    // options; picking a fresh one and pressing "Lưu vai trò mới"
                    // again IS the retry here.
                    <RoleMapStaleCatalogCard
                        appName={
                            vm.apps.find((app) => app.code === staleError.application)?.name ??
                            staleError.application
                        }
                        appRole={staleError.appRole}
                        syncBusy={vm.syncMutation.isPending}
                        onSync={() => {
                            vm.syncMutation.mutate();
                            resetRepointError();
                            setSelected('');
                        }}
                    />
                )}
            </div>
        </Modal>
    );
}
